export type CardGenPhase =
  | 'idle'
  | 'uploading'
  | 'preflighting'
  | 'recap'
  | 'committing'
  | 'processing'
  | 'done'
  | 'error';

export interface CardGenFlowState {
  hasTemplate: boolean;
  hasRecipients: boolean;
  phase: CardGenPhase;
}

/**
 * Map FlowState to a 1-based step index (6 = all done).
 * Steps: 1 template · 2 recipients · 3 output · 4 review · 5 run & download.
 */
export function cardGenStep(s: CardGenFlowState): number {
  if (s.phase === 'done') {
    return 6;
  }
  if (s.phase === 'committing' || s.phase === 'processing') {
    return 5;
  }
  // The pre-flight check (POST + resolve stream) and its recap are the review step.
  if (
    s.phase === 'uploading' ||
    s.phase === 'preflighting' ||
    s.phase === 'recap'
  ) {
    return 4;
  }
  if (s.phase === 'error') {
    return Math.max(2, lastReachedStep(s));
  }
  if (s.hasRecipients) {
    return 3;
  }
  if (s.hasTemplate) {
    return 2;
  }
  return 1;
}

/**
 * When the flow has an error, return the step that failed.
 * Otherwise return undefined.
 */
export function cardGenErrorStep(s: CardGenFlowState): number | undefined {
  if (s.phase !== 'error') {
    return undefined;
  }
  // A pre-flight/render error happens once template + recipients are set → the
  // review step (4). Otherwise surface the last reached selection step.
  if (s.hasTemplate && s.hasRecipients) {
    return 4;
  }
  return lastReachedStep(s);
}

function lastReachedStep(s: CardGenFlowState): number {
  if (s.hasRecipients) {
    return 3;
  }
  if (s.hasTemplate) {
    return 2;
  }
  return 1;
}
